import React, { useEffect, useState } from "react";
import { Loader2, Tag, Copy, CheckCircle2, Sparkles } from "lucide-react";
import api from "../../lib/api";

/**
 * MyPromosTab — customer self-service view of all loyalty promo codes
 * tied to their account (current `<FIRSTNAME>10[-N]` codes + legacy
 * `<FIRSTNAME>5` codes from before the 10% loyalty switch).
 *
 * Each promo is shown as a card with:
 *  - large monospace code + click-to-copy button
 *  - discount %, type (single/multiple), expiry countdown
 *  - status badge (k použití / použito / expirováno)
 * If the user has no codes yet, we show a friendly empty-state
 * explaining that the first 10% code arrives automatically after
 * their first completed detailing.
 */
export default function MyPromosTab() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    api.get("/auth/my-promos")
      .then((r) => setPromos(r.data?.promos || []))
      .catch((e) => setErr(e?.response?.data?.detail || "Nepodařilo se načíst slevy."))
      .finally(() => setLoading(false));
  }, []);

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 1800);
  };

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-apex-gold" /></div>;
  if (err) return <div className="text-red-300 text-sm">{err}</div>;

  return (
    <div data-testid="my-promos-tab">
      <div className="mb-6">
        <h3 className="font-display text-2xl text-apex-cream font-bold flex items-center gap-2">
          <Tag className="w-6 h-6 text-apex-gold" />
          Moje slevy & promo kódy
        </h3>
        <p className="text-apex-cream/65 text-sm mt-2">
          Po každém dokončeném detailingu vám automaticky aktivujeme novou 10% věrnostní slevu na další zakázku. Platnost je 6 měsíců — kódy se vám zde hromadí, dokud je nevyužijete.
        </p>
      </div>

      {promos.length === 0 ? (
        <div className="rounded-2xl border border-apex-line bg-apex-card p-8 text-center" data-testid="my-promos-empty">
          <div className="w-14 h-14 rounded-full bg-apex-gold/15 text-apex-gold flex items-center justify-center mx-auto mb-3">
            <Sparkles className="w-6 h-6" />
          </div>
          <h4 className="text-apex-cream font-semibold text-lg">Zatím žádné slevy</h4>
          <p className="text-sm text-apex-cream/65 mt-2 max-w-md mx-auto">
            Po dokončení vaší <strong className="text-apex-gold">první zakázky</strong> vám automaticky aktivujeme osobní 10% slevový kód — najdete ho zde.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {promos.map((p) => {
            const expiresIn = expiryDays(p.expiresAt);
            const expired = expiresIn !== null && expiresIn < 0;
            const used = p.usedByMe || (p.type === "single" && p.usedCount > 0);
            const disabled = expired || used || !p.isActive;
            return (
              <div
                key={p.code}
                className={`relative rounded-2xl border p-5 transition ${
                  disabled
                    ? "bg-apex-card/40 border-apex-line opacity-60"
                    : "bg-apex-card border-apex-gold/40 hover:border-apex-gold"
                }`}
                data-testid={`promo-card-${p.code}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-apex-cream/55 mb-1">
                      {p.type === "single" ? "Jednorázový" : "Vícenásobný"} · −{p.discount}%
                    </div>
                    <div className="font-mono text-2xl font-bold text-apex-gold tracking-widest">{p.code}</div>
                  </div>
                  <StatusBadge expired={expired} used={used} active={p.isActive} expiresIn={expiresIn} />
                </div>

                <div className="flex items-center justify-between gap-3">
                  <button
                    disabled={disabled}
                    onClick={() => copy(p.code)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-apex-bg border border-apex-line text-apex-cream hover:border-apex-gold hover:text-apex-gold disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`promo-copy-${p.code}`}
                  >
                    {copiedCode === p.code ? <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" /> Zkopírováno</> : <><Copy className="w-3.5 h-3.5" /> Zkopírovat</>}
                  </button>
                  {expiresIn !== null && !expired && (
                    <span className={`text-[11px] ${expiresIn <= 14 ? "text-amber-300" : "text-apex-cream/55"}`}>
                      Platí ještě {expiresIn} {dayWord(expiresIn)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ expired, used, active, expiresIn }) {
  if (used) return <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Použito</span>;
  if (expired) return <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-red-500/15 text-red-300 border border-red-500/30">Expirováno</span>;
  if (!active) return <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-apex-card text-apex-cream/55 border border-apex-line">Neaktivní</span>;
  if (expiresIn !== null && expiresIn <= 14) return <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30">Brzy expiruje</span>;
  return <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-apex-gold/15 text-apex-gold border border-apex-gold/40">K použití</span>;
}

function expiryDays(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const diff = t - Date.now();
  return Math.floor(diff / (24 * 3600 * 1000));
}

function dayWord(n) {
  const abs = Math.abs(n);
  if (abs === 1) return "den";
  if (abs >= 2 && abs <= 4) return "dny";
  return "dní";
}
